<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { refDebounced, useDark } from '@vueuse/core'
import { chatApi } from '@/services/api'
import TextInput from '@/components/common/textInput.vue'
import Loading from '@/components/common/loading.vue'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import giphyAttributionLight from '@/assets/images/giphy/powered-by-giphy-light.png'
import giphyAttributionDark from '@/assets/images/giphy/powered-by-giphy-dark.png'

export type GifSelection = {
  provider: string
  id: string
  url: string
  previewUrl?: string
  description?: string
}

const { t } = useI18n()
const isDark = useDark()

const emits = defineEmits<{
  (e: 'select', gif: GifSelection): void
  (e: 'close'): void
}>()

const search = ref('')
const debounced = refDebounced(search, 350)
const results = ref<GifSelection[]>([])
const loading = ref(false)
const configured = ref(true)
const errorMessage = ref('')

const giphyAttributionSrc = computed(() =>
  isDark.value ? giphyAttributionDark : giphyAttributionLight,
)

const load = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await chatApi.searchGifs({
      q: debounced.value || undefined,
      limit: 24,
    }).promise
    configured.value = response.configured !== false
    results.value = response.results || []
    if (!configured.value) {
      errorMessage.value = t('modules.chat.gif.notConfigured')
    }
  }
  catch (err) {
    console.error(err)
    errorMessage.value = t('modules.chat.gif.loadError')
  }
  finally {
    loading.value = false
  }
}

watch(debounced, load, { immediate: true })
</script>

<template>
  <div
    class="rounded-xl chat-glass-dense border border-black/10 dark:border-white/10 shadow-lg overflow-hidden w-full max-w-md"
    data-cy="chat-gif-picker"
  >
    <div class="flex items-center gap-2 p-2 border-b dark:border-white/10">
      <TextInput
        v-model="search"
        data-cy="chat-gif-search"
        class="flex-1"
        size="small"
        :placeholder="t('modules.chat.gif.search')"
      />
      <button
        type="button"
        class="p-1.5 rounded-sm bg-hover"
        data-cy="chat-gif-picker-close"
        @click="emits('close')"
      >
        <MaterialSymbolsCloseRounded class="w-4 h-4" />
      </button>
    </div>
    <div class="h-56 overflow-y-auto p-2">
      <Loading v-if="loading" />
      <div
        v-else-if="errorMessage"
        class="text-xs opacity-70 p-3 text-center"
        data-cy="chat-gif-error"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="!results.length"
        class="text-xs opacity-70 p-3 text-center"
        data-cy="chat-gif-empty"
      >
        {{ t('modules.chat.gif.empty') }}
      </div>
      <div
        v-else
        class="grid grid-cols-3 gap-1.5"
        data-cy="chat-gif-results"
      >
        <button
          v-for="gif in results"
          :key="gif.id"
          type="button"
          class="aspect-square rounded-lg overflow-hidden bg-hover active:scale-95 transition-transform"
          :data-cy="`chat-gif-item-${gif.id}`"
          @click="emits('select', gif)"
        >
          <img
            :src="gif.previewUrl || gif.url"
            :alt="gif.description || 'gif'"
            class="w-full h-full object-cover"
            loading="lazy"
          >
        </button>
      </div>
    </div>
    <a
      href="https://giphy.com/"
      target="_blank"
      rel="noopener noreferrer"
      class="px-3 py-2 border-t dark:border-white/10 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
      :title="t('modules.chat.gif.poweredBy')"
    >
      <img
        :src="giphyAttributionSrc"
        alt=""
        class="h-6 w-auto max-w-[140px] object-contain"
      >
      <span class="text-[10px] opacity-70 whitespace-nowrap">
        {{ t('modules.chat.gif.poweredBy') }}
      </span>
    </a>
  </div>
</template>
